import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  ArrowLeft,
  AlertCircle,
  Calendar,
  CheckCircle2,
  Circle,
  MapPin,
  Clock,
  ExternalLink,
  Loader2,
  Filter,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { format } from "date-fns";

const formatDateSafe = (dateStr, fmt = "MMM d, yyyy") => {
  if (!dateStr) return "";
  try {
    const s = String(dateStr);
    const datePart = s.includes("T") ? s.split("T")[0] : s.split(" ")[0];
    const [y, m, d] = datePart.split("-").map(Number);
    if (!y || !m || !d) return "";
    return format(new Date(y, m - 1, d), fmt);
  } catch {
    return "";
  }
};

const TasksView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeFilter = searchParams.get("filter") || "overdue";
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard/tasks?filter=${activeFilter}`, {
        withCredentials: true,
      });
      setTasks(res.data.tasks || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [activeFilter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleComplete = async (task) => {
    setCompleting(task.task_id);
    try {
      await axios.put(
        `${API}/transactions/${task.transaction_id}/tasks/${task.task_id}`,
        { status: "completed" },
        { withCredentials: true }
      );
      toast.success(`"${task.title}" marked complete`);
      setTasks((prev) => prev.filter((t) => t.task_id !== task.task_id));
      setTotal((prev) => prev - 1);
    } catch {
      toast.error("Failed to complete task");
    } finally {
      setCompleting(null);
    }
  };

  const getAssigneeLabel = (task) => {
    if (task.assigned_coordinator_name) return task.assigned_coordinator_name;
    const type = task.assigned_to_type;
    if (!type) return null;
    if (type.startsWith("coordinator:")) return "Coordinator";
    return type.replace(/_/g, " ");
  };

  const isOverdue = activeFilter === "overdue";

  return (
    <div className="p-6 md:p-8 animate-fade-in">
      {/* Header */}
      <div className="page-header -mx-6 md:-mx-8 -mt-6 md:-mt-8 mb-6 px-6 md:px-8 py-6">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--t-text)] [font-family:var(--t-heading-font)] tracking-tight">
              {isOverdue ? "Overdue Tasks" : "Upcoming Tasks"}
            </h1>
            <p className="text-[var(--t-text-muted)] mt-1">
              {total} task{total !== 1 ? "s" : ""} across all transactions
            </p>
          </div>
          {/* Filter toggle */}
          <div className="flex gap-2" data-testid="task-filter-toggle">
            <Button
              variant={isOverdue ? "default" : "outline"}
              size="sm"
              onClick={() => setSearchParams({ filter: "overdue" })}
              className={isOverdue ? "bg-red-600 hover:bg-red-700 text-white" : ""}
              data-testid="filter-overdue-btn"
            >
              <AlertCircle className="w-4 h-4 mr-1.5" />
              Overdue
            </Button>
            <Button
              variant={!isOverdue ? "default" : "outline"}
              size="sm"
              onClick={() => setSearchParams({ filter: "upcoming" })}
              className={!isOverdue ? "bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)] text-white" : ""}
              data-testid="filter-upcoming-btn"
            >
              <Calendar className="w-4 h-4 mr-1.5" />
              Upcoming
            </Button>
          </div>
        </div>
      </div>

      {/* Task List */}
      <Card className="card-surface" data-testid="tasks-list-card">
        <CardHeader className="border-b border-[var(--t-border)] py-3 px-5">
          <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider">
            <div className="col-span-1"></div>
            <div className="col-span-4">Task</div>
            <div className="col-span-3">Property</div>
            <div className="col-span-2">Assignee</div>
            <div className="col-span-1">Due Date</div>
            <div className="col-span-1"></div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--t-text-subtle)]" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="empty-state py-16">
              <CheckCircle2 className="empty-state-icon text-green-500" />
              <p className="empty-state-title">
                {isOverdue ? "No overdue tasks" : "No upcoming tasks"}
              </p>
              <p className="empty-state-description">
                {isOverdue
                  ? "Great job! All tasks are on track."
                  : "No tasks due in the next 14 days."}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-[var(--t-border-light)]">
              {tasks.map((task, index) => (
                <div
                  key={task.task_id}
                  className={`grid grid-cols-12 gap-4 items-center px-5 py-3.5 hover:bg-[var(--t-bg)] transition-colors ${
                    isOverdue ? "hover:bg-red-50/50" : ""
                  }`}
                  data-testid={`task-row-${index}`}
                >
                  {/* Complete button */}
                  <div className="col-span-1 flex justify-center">
                    <button
                      onClick={() => handleComplete(task)}
                      disabled={completing === task.task_id}
                      className="group"
                      data-testid={`complete-task-${index}`}
                    >
                      {completing === task.task_id ? (
                        <Loader2 className="w-5 h-5 animate-spin text-[var(--t-text-subtle)]" />
                      ) : isOverdue ? (
                        <AlertCircle className="w-5 h-5 text-red-400 group-hover:text-green-500 transition-colors" />
                      ) : (
                        <Circle className="w-5 h-5 text-slate-300 group-hover:text-green-500 transition-colors" />
                      )}
                    </button>
                  </div>

                  {/* Task title */}
                  <div className="col-span-4 min-w-0">
                    <p className="text-sm font-medium text-[var(--t-text)] truncate">
                      {task.title}
                    </p>
                  </div>

                  {/* Property */}
                  <div className="col-span-3 min-w-0">
                    <Link
                      to={`/transactions/${task.transaction_id}`}
                      className="flex items-center gap-1.5 text-sm text-[var(--t-primary)] hover:underline truncate group"
                      data-testid={`task-property-link-${index}`}
                    >
                      <MapPin className="w-3.5 h-3.5 shrink-0 opacity-60" />
                      <span className="truncate">{task.property_address}</span>
                    </Link>
                  </div>

                  {/* Assignee */}
                  <div className="col-span-2">
                    {getAssigneeLabel(task) ? (
                      <Badge variant="outline" className="text-xs capitalize font-normal">
                        {getAssigneeLabel(task)}
                      </Badge>
                    ) : (
                      <span className="text-xs text-[var(--t-text-subtle)]">Unassigned</span>
                    )}
                  </div>

                  {/* Due date */}
                  <div className="col-span-1">
                    <span
                      className={`text-xs font-medium ${
                        isOverdue ? "text-red-600" : "text-[var(--t-text-muted)]"
                      }`}
                    >
                      {formatDateSafe(task.due_date, "MMM d")}
                    </span>
                  </div>

                  {/* Go to transaction */}
                  <div className="col-span-1 flex justify-end">
                    <Link
                      to={`/transactions/${task.transaction_id}`}
                      className="text-[var(--t-text-subtle)] hover:text-[var(--t-primary)] transition-colors"
                      title="View Transaction"
                      data-testid={`go-to-transaction-${index}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TasksView;
